import { useState } from 'react';
import MunicipioMap from './MunicipioMap';
import FreguesiaTable from './FreguesiaTable';
import type { ReplayDoc } from '../../lib/replay';
import type { Concelho } from '../../lib/concelhos';

// The comparison the product exists to make: on a day when the official index gave ONE
// class to the whole concelho, our 1 km grid still varied across it.
//
// Art. 43 (DL 82/2021) is respected structurally: the official class is shown first and
// is never contradicted, only sub-divided. We are not a competing index.
//
// The provenance fold is deliberately blunt, including what the ledger does NOT prove.
// The audience for this page includes people who price risk for a living; a claim that
// does not survive scrutiny is worse than no claim.

export default function ReplayPanel({
  doc,
  concelho,
}: {
  doc: ReplayDoc;
  concelho: Concelho;
}) {
  const [showProof, setShowProof] = useState(false);
  const spread = doc.ignis.spread_max_over_min;
  const cells = doc.cells.map((c) => [c[0], c[1], c[2], c[3], null]) as number[][];

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-ink-700 bg-ink-900 p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-xl font-bold text-mist">
            {doc.date} · o mesmo dia, duas resoluções
          </h2>
          <span className="font-mono text-xs text-mist-faint">
            {doc.model_version} · ciclo {doc.cycle}
          </span>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-ink-700 bg-ink-950 p-4">
            <div className="font-mono text-xs uppercase tracking-widest text-mist-faint">
              Índice oficial IPMA/ICNF
            </div>
            <div className="mt-2 font-display text-2xl font-bold text-mist">
              {doc.oficial.d0_label}
            </div>
            <div className="mt-1 text-sm text-mist-muted">{doc.oficial.ambito}</div>
            <div className="mt-3 text-xs leading-relaxed text-mist-faint">
              É o índice oficial e é a referência legal. Não o contestamos.
            </div>
          </div>

          <div className="rounded-lg border border-ember/40 bg-ember/5 p-4">
            <div className="font-mono text-xs uppercase tracking-widest text-ember">
              Ignis · grelha de 1 km
            </div>
            <div className="mt-2 font-display text-2xl font-bold text-mist">
              {spread != null ? `${spread}x` : '—'}
            </div>
            <div className="mt-1 text-sm text-mist-muted">
              de variação dentro do mesmo concelho
            </div>
            <div className="mt-3 font-mono text-xs text-mist-faint">
              {doc.ignis.n_cells} células · {doc.ignis.risk.Moderate ?? 0} em Moderado ·
              máx {doc.ignis.max_prob.toFixed(4)} / mín {doc.ignis.min_prob.toFixed(4)}
            </div>
          </div>
        </div>

        <p className="mt-5 border-l-2 border-ember pl-4 text-sm leading-relaxed text-mist">
          O índice oficial diz-lhe <span className="font-semibold">o dia</span>. Nós
          dizemos-lhe <span className="font-semibold">o sítio</span>.
        </p>
      </div>

      <div className="rounded-xl border border-ink-700 bg-ink-900 p-6">
        <div className="font-mono text-xs uppercase tracking-widest text-mist-faint">
          Onde estava o risco nesse dia
        </div>
        <div className="mt-3">
          <MunicipioMap
            slug={concelho.slug}
            bbox={concelho.bbox}
            cells={cells}
            viewMode="risk"
            height="h-80"
          />
        </div>
        <div className="mt-5">
          <FreguesiaTable rows={doc.freguesias} />
        </div>
      </div>

      <div className="rounded-xl border border-ink-700 bg-ink-900 p-6">
        <button
          onClick={() => setShowProof((v) => !v)}
          className="flex w-full items-center justify-between text-left"
        >
          <span className="font-display text-lg font-bold text-mist">
            Como verificar estes números
          </span>
          <span className="font-mono text-xs text-ember">{showProof ? '−' : '+'}</span>
        </button>

        {showProof && (
          <div className="mt-4 space-y-4 text-sm leading-relaxed text-mist-muted">
            <p>
              Esta página não lhe pede que acredite em nós. Os números acima são
              recalculados a partir de ficheiros públicos e imutáveis no nosso arquivo
              aberto. Descarregue-os, verifique o SHA-256 e refaça as contas.
            </p>

            <ul className="space-y-3">
              {doc.provenance.sources.map((s) => (
                <li key={s.caminho} className="rounded-lg border border-ink-700 bg-ink-950 p-3">
                  <div className="text-mist">{s.o_que}</div>
                  <a
                    href={s.permalink}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="mt-1 block break-all font-mono text-xs text-ember hover:underline"
                  >
                    {s.caminho}
                  </a>
                  <div className="mt-1 break-all font-mono text-[11px] text-mist-faint">
                    sha256 {s.sha256}
                  </div>
                  <div className="mt-0.5 font-mono text-[11px] text-mist-faint">
                    publicado {s.publicado_em} · commit {s.commit.slice(0, 12)}
                  </div>
                </li>
              ))}
            </ul>

            <div className="rounded-lg border border-ink-700 bg-ink-950 p-3">
              <div className="font-semibold text-mist">O que isto prova</div>
              <p className="mt-1">{doc.provenance.o_que_isto_prova}</p>
            </div>

            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
              <div className="font-semibold text-mist">O que isto não prova</div>
              <p className="mt-1">{doc.provenance.o_que_isto_NAO_prova}</p>
            </div>

            <p>
              O registo medido, prospetivo e independente deste dia está em{' '}
              <a
                href={doc.provenance.validacao}
                className="text-ember hover:brightness-110"
                target="_blank"
                rel="noreferrer noopener"
              >
                /ignis/validacao
              </a>
              . O arquivo completo:{' '}
              <a
                href={doc.provenance.ledger}
                className="break-all text-ember hover:brightness-110"
                target="_blank"
                rel="noreferrer noopener"
              >
                {doc.provenance.ledger}
              </a>
              .
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
